import React, { useEffect, useMemo, useState } from 'react';
import styles from './PaymentHistory.module.css';
import Card from '../components/Card';
import Badge from '../components/Badge';
import Button from '../components/Button';
import EmptyState from '../components/EmptyState';
import Input from '../components/Input';
import Select from '../components/Select';
import StatCard from '../components/StatCard';
import Table from '../components/Table';
import { useOrders } from '../hooks';
import { paymentService } from '../services/api';
import { useToast } from '../store/ToastContext';
import { formatCurrency, formatDate, formatDateTime, capitalizeFirst } from '../utils/helpers';
import {
  DownloadIcon,
  EyeIcon,
  IndianRupeeIcon,
  PaymentIcon,
  CheckCircleIcon,
  SearchIcon,
  TransactionIcon,
  XIcon,
} from '../components/Icons';

const PAGE_SIZE = 10;

// Order statuses grouped into what the customer cares about
const STATUS = {
  completed: { label: 'Paid', variant: 'success' },
  pending: { label: 'Pending', variant: 'warning' },
  created: { label: 'Pending', variant: 'warning' },
  processing: { label: 'Pending', variant: 'warning' },
  failed: { label: 'Failed', variant: 'error' },
  cancelled: { label: 'Cancelled', variant: 'default' },
};

const STATUS_FILTERS = [
  { value: '', label: 'All Status' },
  { value: 'Paid', label: 'Paid' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Failed', label: 'Failed' },
  { value: 'Cancelled', label: 'Cancelled' },
];

const PAYMENT_METHODS = {
  upi: 'UPI',
  card: 'Card',
  netbanking: 'Net Banking',
  wallet: 'Wallet',
  emi: 'EMI',
};

function getStatus(status) {
  return STATUS[status?.toLowerCase()] || { label: status ? capitalizeFirst(status) : 'Unknown', variant: 'default' };
}

function getPaymentMethod(method) {
  if (!method || method === 'unknown') return '—';
  return PAYMENT_METHODS[method.toLowerCase()] || capitalizeFirst(method);
}

// Paid orders are dated by payment time, everything else by when it was started
function getOrderDate(order) {
  return order.completed_at || order.created_at;
}

function getAmounts(order) {
  const total = Number(order.total_amount) || 0;
  return {
    subtotal: Number(order.subtotal ?? total) || 0,
    gst: Number(order.gst_amount) || 0,
    gstRate: Number(order.gst_rate) || 0,
    total,
  };
}

function toCsvDate(value) {
  const d = new Date(value);
  if (!value || isNaN(d.getTime())) return '';
  const pad = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function csvCell(value) {
  const str = String(value ?? '');
  return /[",\r\n]/.test(str) ? `"${str.replace(/"/g, '""')}"` : str;
}

function saveFile(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

function PaymentDetailsModal({ order, onClose, onDownloadInvoice, downloading }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const status = getStatus(order.status);
  const amounts = getAmounts(order);
  const isPaid = order.status === 'completed';

  const details = [
    ['Plan', order.plan_name || '—'],
    ['Licences', order.number_of_dongles],
    ['Order Date', formatDateTime(order.created_at)],
    ['Paid On', isPaid ? formatDateTime(order.completed_at) : '—'],
    ['Payment Method', getPaymentMethod(order.payment_method)],
    ['Payment ID', order.razorpay_payment_id || '—', true],
    ['Transaction ID', order.transaction_id || '—', true],
    ['Razorpay Order ID', order.razorpay_order_id || '—', true],
  ];

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div
        className={styles.modal}
        role="dialog"
        aria-modal="true"
        aria-labelledby="payment-details-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className={styles.modalHeader}>
          <div>
            <h2 id="payment-details-title" className={styles.modalTitle}>Payment Details</h2>
            <span className={styles.mono}>{order.order_number}</span>
          </div>
          <div className={styles.modalHeaderRight}>
            <Badge variant={status.variant}>{status.label}</Badge>
            <button className={styles.closeButton} onClick={onClose} aria-label="Close" autoFocus>
              <XIcon size={16} />
            </button>
          </div>
        </div>

        <div className={styles.modalBody}>
          <dl className={styles.detailGrid}>
            {details.map(([label, value, mono]) => (
              <div key={label} className={styles.detailItem}>
                <dt>{label}</dt>
                <dd className={mono ? styles.mono : undefined}>{value}</dd>
              </div>
            ))}
          </dl>

          {order.failure_reason && (
            <p className={styles.failureReason}>{order.failure_reason}</p>
          )}

          <div className={styles.amountBox}>
            <div className={styles.amountRow}>
              <span>Subtotal</span>
              <span>{formatCurrency(amounts.subtotal)}</span>
            </div>
            <div className={styles.amountRow}>
              <span>GST{amounts.gstRate ? ` (${amounts.gstRate}%)` : ''}</span>
              <span>{formatCurrency(amounts.gst)}</span>
            </div>
            <div className={`${styles.amountRow} ${styles.amountTotal}`}>
              <span>Total</span>
              <span>{formatCurrency(amounts.total)}</span>
            </div>
          </div>
        </div>

        <div className={styles.modalFooter}>
          {isPaid && (
            <Button
              variant="primary"
              leftIcon={<DownloadIcon />}
              onClick={() => onDownloadInvoice(order)}
              disabled={downloading}
            >
              Download Invoice
            </Button>
          )}
          <Button variant="secondary" onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}

function PaymentHistory() {
  const toast = useToast();
  const { orders, loading, error, refetch } = useOrders();

  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [selected, setSelected] = useState(null);
  const [invoiceBusy, setInvoiceBusy] = useState(null);

  const summary = useMemo(() => {
    const paid = orders.filter((o) => o.status === 'completed');
    const last = paid.reduce((latest, o) => (
      !latest || new Date(getOrderDate(o)) > new Date(getOrderDate(latest)) ? o : latest
    ), null);
    return {
      totalPaid: paid.reduce((sum, o) => sum + getAmounts(o).total, 0),
      gstPaid: paid.reduce((sum, o) => sum + getAmounts(o).gst, 0),
      count: paid.length,
      lastPaidOn: last ? getOrderDate(last) : null,
    };
  }, [orders]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const from = dateFrom ? new Date(`${dateFrom}T00:00:00`) : null;
    const to = dateTo ? new Date(`${dateTo}T23:59:59.999`) : null;

    return orders.filter((o) => {
      if (statusFilter && getStatus(o.status).label !== statusFilter) return false;
      if (from || to) {
        const d = new Date(getOrderDate(o));
        if (from && d < from) return false;
        if (to && d > to) return false;
      }
      if (!q) return true;
      return [o.order_number, o.plan_name, o.razorpay_payment_id, o.transaction_id]
        .some((v) => (v || '').toLowerCase().includes(q));
    });
  }, [orders, search, statusFilter, dateFrom, dateTo]);

  useEffect(() => { setPage(1); }, [search, statusFilter, dateFrom, dateTo]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageRows = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const hasFilters = Boolean(search || statusFilter || dateFrom || dateTo);

  const clearFilters = () => {
    setSearch('');
    setStatusFilter('');
    setDateFrom('');
    setDateTo('');
  };

  const handleDownloadInvoice = async (order) => {
    if (invoiceBusy) return;
    setInvoiceBusy(order.order_number);
    try {
      const response = await paymentService.downloadInvoice(order.order_number);
      saveFile(response.data, `Invoice-${order.order_number}.pdf`);
    } catch (err) {
      toast.error('Could not download the invoice. Please try again later.');
    } finally {
      setInvoiceBusy(null);
    }
  };

  const handleExport = () => {
    if (filtered.length === 0) return;
    const headers = [
      'Order Number', 'Date', 'Plan', 'Licences', 'Subtotal (INR)', 'GST (INR)',
      'Total (INR)', 'Status', 'Payment Method', 'Payment ID',
    ];
    const rows = filtered.map((o) => {
      const a = getAmounts(o);
      return [
        o.order_number, toCsvDate(getOrderDate(o)), o.plan_name || '', o.number_of_dongles,
        a.subtotal.toFixed(2), a.gst.toFixed(2), a.total.toFixed(2), getStatus(o.status).label,
        getPaymentMethod(o.payment_method).replace('—', ''), o.razorpay_payment_id || '',
      ];
    });
    const csv = [headers, ...rows].map((r) => r.map(csvCell).join(',')).join('\r\n');
    // BOM so Excel opens the file as UTF-8
    saveFile(new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' }),
      `payment-history_${toCsvDate(new Date()).slice(0, 10)}.csv`);
    toast.success(`Exported ${filtered.length} payment${filtered.length === 1 ? '' : 's'}`);
  };

  return (
    <div className={styles.container}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Payment History</h1>
          <p className={styles.subtitle}>View your payments and export records</p>
        </div>
        <Button
          variant="secondary"
          leftIcon={<DownloadIcon />}
          onClick={handleExport}
          disabled={loading || filtered.length === 0}
        >
          Export CSV
        </Button>
      </header>

      <div className={styles.statsGrid}>
        <StatCard
          title="Total Paid"
          value={formatCurrency(summary.totalPaid)}
          subtitle="Including GST"
          icon={<IndianRupeeIcon />}
          variant="primary"
        />
        <StatCard
          title="GST Paid"
          value={formatCurrency(summary.gstPaid)}
          subtitle="Across all payments"
          icon={<PaymentIcon />}
          variant="warning"
        />
        <StatCard
          title="Successful Payments"
          value={summary.count}
          subtitle={summary.lastPaidOn ? `Last paid ${formatDate(summary.lastPaidOn)}` : 'No payments yet'}
          icon={<CheckCircleIcon />}
          variant="success"
        />
      </div>

      <Card padding="none">
        <div className={styles.toolbar}>
          <Input
            placeholder="Search order no., plan or payment ID..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            leftIcon={<SearchIcon />}
            containerClassName={styles.search}
          />
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            options={STATUS_FILTERS}
            placeholder={null}
            fullWidth={false}
            containerClassName={styles.status}
            aria-label="Filter by status"
          />
          <Input
            type="date"
            label="From"
            value={dateFrom}
            max={dateTo || undefined}
            onChange={(e) => setDateFrom(e.target.value)}
            fullWidth={false}
            containerClassName={styles.date}
          />
          <Input
            type="date"
            label="To"
            value={dateTo}
            min={dateFrom || undefined}
            onChange={(e) => setDateTo(e.target.value)}
            fullWidth={false}
            containerClassName={styles.date}
          />
          {hasFilters && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              Clear filters
            </Button>
          )}
        </div>

        {loading ? (
          <Table.Skeleton rows={5} columns={7} />
        ) : error ? (
          <EmptyState
            icon={<TransactionIcon size={36} />}
            title="Could not load payment history"
            description={error}
            action={refetch}
            actionLabel="Try again"
          />
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={<TransactionIcon size={36} />}
            title={hasFilters ? 'No payments match your filters' : 'No payments yet'}
            description={hasFilters ? 'Try adjusting or clearing the filters' : 'Your payments will appear here after you purchase a plan'}
          />
        ) : (
          <>
            <Table>
              <Table.Head>
                <Table.Row>
                  <Table.Header>Order No.</Table.Header>
                  <Table.Header>Date</Table.Header>
                  <Table.Header>Plan</Table.Header>
                  <Table.Header align="center">Licences</Table.Header>
                  <Table.Header align="right">Amount</Table.Header>
                  <Table.Header align="center">Status</Table.Header>
                  <Table.Header align="right">Actions</Table.Header>
                </Table.Row>
              </Table.Head>
              <Table.Body>
                {pageRows.map((o) => {
                  const status = getStatus(o.status);
                  return (
                    <Table.Row key={o.id}>
                      <Table.Cell><span className={styles.mono}>{o.order_number}</span></Table.Cell>
                      <Table.Cell>{formatDate(getOrderDate(o))}</Table.Cell>
                      <Table.Cell>{o.plan_name || '—'}</Table.Cell>
                      <Table.Cell align="center">{o.number_of_dongles}</Table.Cell>
                      <Table.Cell align="right">
                        <span className={styles.amount}>{formatCurrency(getAmounts(o).total)}</span>
                      </Table.Cell>
                      <Table.Cell align="center"><Badge variant={status.variant}>{status.label}</Badge></Table.Cell>
                      <Table.Cell align="right">
                        <div className={styles.rowActions}>
                          <Button variant="ghost" size="sm" leftIcon={<EyeIcon />} onClick={() => setSelected(o)}>
                            View
                          </Button>
                          {o.status === 'completed' && (
                            <Button
                              variant="ghost"
                              size="sm"
                              leftIcon={<DownloadIcon />}
                              onClick={() => handleDownloadInvoice(o)}
                              disabled={invoiceBusy === o.order_number}
                            >
                              Invoice
                            </Button>
                          )}
                        </div>
                      </Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>

            {filtered.length > PAGE_SIZE && (
              <div className={styles.pagination}>
                <span className={styles.pageInfo}>
                  {(currentPage - 1) * PAGE_SIZE + 1}–{Math.min(currentPage * PAGE_SIZE, filtered.length)} of {filtered.length}
                </span>
                <div className={styles.pageButtons}>
                  <Button variant="secondary" size="sm" disabled={currentPage === 1} onClick={() => setPage(currentPage - 1)}>
                    Previous
                  </Button>
                  <Button variant="secondary" size="sm" disabled={currentPage === totalPages} onClick={() => setPage(currentPage + 1)}>
                    Next
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </Card>

      {selected && (
        <PaymentDetailsModal
          order={selected}
          onClose={() => setSelected(null)}
          onDownloadInvoice={handleDownloadInvoice}
          downloading={invoiceBusy === selected.order_number}
        />
      )}
    </div>
  );
}

export default PaymentHistory;
